import { useState, useCallback, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Box,
  Backdrop,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import { AppLayout } from '@base/ui/layouts/app-layout';
import { AppHeader } from '@base/ui/components/app-header';
import { ProductInfoCard } from '../ticket-list/product-info-card';
import { DefectLinesList } from './defect-lines-list';
import { NotesTextarea } from './notes-textarea';
import { useCreateTicket } from './use-create-ticket';
import { useImageUpload } from './use-image-upload';
import { createTicketFormSchema, type CreateTicketFormData } from '@core/utils/validation';
import { triggerDeepLink } from '@core/utils/deep-link';
import { useNavigationStore } from '../router/navigation-store';
import { useBackButton } from '../router/use-back-button';
import { ROUTES } from '../router/route-constants';
import type { FieldTicketRequest, DefectLineInput } from '@core/types/ticket';

type SubmitStep = 'idle' | 'validating' | 'uploading' | 'creating' | 'success' | 'error';

interface SubmitState {
  step: SubmitStep;
  progress: number;
  message: string;
}

export function CreateTicketPage() {
  const navigate = useNavigate();
  const urlParams = useNavigationStore((state) => state.urlParams);
  const resolvedItemCode = useNavigationStore((state) => state.resolvedItemCode);
  const createTicketMutation = useCreateTicket();
  const imageUpload = useImageUpload();
  const cancelRequestedRef = useRef(false);

  const [submitState, setSubmitState] = useState<SubmitState>({
    step: 'idle',
    progress: 0,
    message: '',
  });

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const methods = useForm<CreateTicketFormData>({
    resolver: zodResolver(createTicketFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      defectLines: [
        {
          lineType: 'Machine',
          componentItemCode: null,
          isOtherComponent: false,
          otherComponentDescription: '',
          marketDefect: null,
          isOtherDefect: false,
          otherDefectDescription: '',
          marketDefectCause: null,
          isOtherCause: false,
          otherCauseDescription: '',
          apiDefectLineName: undefined,
          images: [],
        },
      ],
      notes: '',
    },
  });

  const showToast = useCallback((message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const requestSubmitAbort = useCallback(() => {
    cancelRequestedRef.current = true;
    setSubmitState({ step: 'error', progress: 0, message: 'Đã hủy gửi báo cáo' });
    showToast('Đã hủy gửi báo cáo', 'error');
  }, [showToast]);
  const { goBack } = useBackButton();

  const handleCancel = useCallback(() => {
    goBack();
  }, [goBack]);

  const transformToApiLine = (line: CreateTicketFormData['defectLines'][number]): DefectLineInput => ({
    line_type: line.lineType,
    component_item_code: line.componentItemCode || undefined,
    is_other_component: line.isOtherComponent || false,
    other_component_description: line.otherComponentDescription || undefined,
    market_defect: line.marketDefect || undefined,
    is_other_defect: line.isOtherDefect || false,
    other_defect_description: line.otherDefectDescription || undefined,
    market_defect_cause: line.marketDefectCause || undefined,
    is_other_cause: line.isOtherCause || false,
    other_cause_description: line.otherCauseDescription || undefined,
  });

  const getImageFiles = (line: CreateTicketFormData['defectLines'][number], lineIndex: number): File[] => {
    const rawImages = line.images ?? [];
    console.log(`[CreateTicket] 📸 Line ${lineIndex}: ${rawImages.length} raw images`);

    const files = rawImages
      .map((image) => {
        const maybeFile = image as File | { file: File };
        return maybeFile instanceof File ? maybeFile : maybeFile.file;
      })
      .filter((file): file is File => {
        const isValid = file instanceof File;
        if (isValid) {
          console.log(`[CreateTicket] 📎 File: ${file.name}, size=${file.size}, type=${file.type}`);
        } else {
          console.warn('[CreateTicket] ⚠️ Invalid file object:', file);
        }
        return isValid;
      });

    return files;
  };

  const handleValidationError = useCallback((_errors: FieldErrors<CreateTicketFormData>) => {
    window.setTimeout(() => {
      const firstInvalidField = document.querySelector<HTMLElement>(
        '[aria-invalid="true"], .Mui-error input, .Mui-error textarea'
      );
      firstInvalidField?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }, 0);
  }, []);

  const handleOpenConfirmDialog = methods.handleSubmit(() => {
    setShowConfirmDialog(true);
  }, handleValidationError);

  const handleCancelConfirm = useCallback(() => {
    setShowConfirmDialog(false);
  }, []);

  const handleProceedSubmit = useCallback(async () => {
    setShowConfirmDialog(false);
    cancelRequestedRef.current = false;

    if (!urlParams) {
      showToast('Thiếu thông tin URL params', 'error');
      return;
    }

    const formData = methods.getValues();
    const submitProgress = { currentStep: 'validating' as SubmitStep };
    const updateSubmitState = (state: SubmitState) => {
      submitProgress.currentStep = state.step;
      setSubmitState(state);
    };
    const throwIfCancelled = () => {
      if (cancelRequestedRef.current) {
        throw new Error('Đã hủy gửi báo cáo');
      }
    };

    updateSubmitState({ step: 'validating', progress: 10, message: 'Đang kiểm tra dữ liệu...' });

    try {
      // Step 1: Validate
      updateSubmitState({ step: 'validating', progress: 15, message: 'Đang kiểm tra dữ liệu...' });
      await new Promise((resolve) => setTimeout(resolve, 200)); // Brief delay for UX
      throwIfCancelled();

      const defectLinesForApi = formData.defectLines.map(transformToApiLine);

      // Step 2: Create ticket first so iMES returns real defect line IDs
      updateSubmitState({ step: 'creating', progress: 35, message: 'Đang tạo báo cáo...' });

      const request: FieldTicketRequest = {
        ticket_id_external: urlParams.incident_id,
        item_code: resolvedItemCode || urlParams.item_code || '',
        serial_no: urlParams.serial_no,
        reported_date: new Date().toISOString().split('T')[0],
        technician_id: urlParams.technician_id,
        technician_name: urlParams.technician_name,
        customer_name: urlParams.customer_name,
        customer_phone: urlParams.customer_phone,
        customer_address: urlParams.customer_address,
        notes: formData.notes,
        defect_lines: defectLinesForApi,
      };

      const response = await createTicketMutation.createTicket(request);
      throwIfCancelled();

      // Step 3: Upload images with real ticket_name and defect_line_name
      const linesWithImages = formData.defectLines
        .map((line, index) => ({
          files: getImageFiles(line, index),
          defectLineName: response.defect_lines[index]?.name,
          index,
        }))
        .filter((line) => line.files.length > 0);

      if (linesWithImages.length > 0) {
        updateSubmitState({ step: 'uploading', progress: 65, message: 'Đang tải ảnh lên...' });

        for (let i = 0; i < linesWithImages.length; i++) {
          throwIfCancelled();
          const { files, defectLineName, index } = linesWithImages[i];

          if (!defectLineName) {
            throw new Error(`Không tìm thấy mã dòng lỗi #${index + 1} để tải ảnh`);
          }

          await imageUpload.uploadImages(files, {
            ticketName: response.name,
            defectLineName,
          });
          throwIfCancelled();

          const progress = 65 + ((i + 1) / linesWithImages.length) * 25;
          updateSubmitState({
            step: 'uploading',
            progress,
            message: `Đang tải ảnh lên... (${i + 1}/${linesWithImages.length} dòng)`,
          });
        }
      }
      throwIfCancelled();

      // Step 4: Success
      updateSubmitState({ step: 'success', progress: 100, message: 'Hoàn tất!' });
      showToast('✅ Gửi báo cáo thành công', 'success');

      // Trigger deep link after short delay for UX
      setTimeout(() => {
        if (cancelRequestedRef.current) return;
        navigate(ROUTES.LIST);
        triggerDeepLink({ action: 'created', ticket_id: response.name, ticket_count: 1 });
      }, 800);
    } catch (error) {
      if (cancelRequestedRef.current) {
        setSubmitState({ step: 'error', progress: 0, message: 'Đã hủy gửi báo cáo' });
        showToast('Đã hủy gửi báo cáo', 'error');
        return;
      }

      // Extract API error message from Error or AppError (plain object from normalizeError)
      const apiMessage =
        error instanceof Error
          ? error.message
          : (error && typeof error === 'object' && 'message' in error)
            ? (error as { message: string }).message
            : null;

      let errorMessage: string;
      if (submitProgress.currentStep === 'uploading') {
        errorMessage = 'Tải ảnh lên thất bại';
      } else {
        errorMessage = 'Tạo báo cáo thất bại';
      }

      // Append API message if available
      if (apiMessage) {
        errorMessage = `${errorMessage}: ${apiMessage}`;
      }

      setSubmitState({ step: 'error', progress: 0, message: errorMessage });
      showToast(errorMessage, 'error');

      // Enhanced error logging for WebView debugging
      console.error(
        `[CreateTicket] ❌ Error at step "${submitProgress.currentStep}":`,
        error instanceof Error
          ? { message: error.message, stack: error.stack }
          : JSON.stringify(error, null, 2)
      );
    }
  }, [urlParams, methods, imageUpload, createTicketMutation, showToast, navigate]);

  const isBusy = submitState.step !== 'idle' && submitState.step !== 'error' && submitState.step !== 'success';
  const hasValidationErrors = methods.formState.isSubmitted && Object.keys(methods.formState.errors).length > 0;

  const getBackdropContent = () => {
    if (submitState.step === 'success') {
      return (
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
            Thành công!
          </Typography>
          <Typography sx={{ color: '#fff' }}>
            Đang chuyển hướng...
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ textAlign: 'center', width: 300 }}>
        <CircularProgress color="inherit" sx={{ mb: 2 }} />
        <Typography variant="h6" sx={{ mb: 1, color: '#fff' }}>
          Đang gửi báo cáo...
        </Typography>
        <Typography sx={{ mb: 2, color: '#fff' }}>
          {submitState.message}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={submitState.progress}
          sx={{
            width: '100%',
            bgcolor: 'rgba(255,255,255,0.2)',
            '& .MuiLinearProgress-bar': {
              bgcolor: '#fff',
            },
          }}
        />
        <Button
          variant="outlined"
          color="inherit"
          onClick={requestSubmitAbort}
          sx={{ mt: 2 }}
        >
          Hủy gửi
        </Button>
      </Box>
    );
  };

  return (
    <FormProvider {...methods}>
      <AppLayout
        header={
          <AppHeader
            title="Tạo báo cáo lỗi mới"
            onBack={isBusy ? undefined : handleCancel}
          />
        }
        bottomBar={
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              px: 2,
              py: 1,
              height: '100%',
              alignItems: 'center',
            }}
          >
            <Button
              variant="outlined"
              fullWidth
              onClick={handleCancel}
              disabled={isBusy}
            >
              Hủy
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={handleOpenConfirmDialog}
              disabled={isBusy}
            >
              Gửi báo cáo
            </Button>
          </Box>
        }
      >
        <Box sx={{ p: 2 }}>
          <ProductInfoCard />
          {hasValidationErrors && (
            <Alert severity="error" sx={{ mb: 2 }} data-error-summary="true">
              Vui lòng kiểm tra lại các thông tin bắt buộc trước khi gửi báo cáo.
            </Alert>
          )}
          <DefectLinesList disabled={isBusy} />
          <NotesTextarea disabled={isBusy} />
        </Box>
      </AppLayout>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelConfirm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Xác nhận gửi báo cáo</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bạn có chắc chắn muốn gửi báo cáo lỗi này? Sau khi gửi, báo cáo sẽ được xử lý và không thể chỉnh sửa trực tiếp.
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Số dòng lỗi: <strong>{methods.getValues('defectLines').length}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tổng số ảnh:{' '}
              <strong>
                {methods.getValues('defectLines').reduce(
                  (total, line) => total + (line.images?.length || 0),
                  0
                )}
              </strong>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirm} color="inherit">
            Hủy
          </Button>
          <Button onClick={handleProceedSubmit} variant="contained" disabled={isBusy} autoFocus>
            Xác nhận gửi
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}
        open={isBusy || submitState.step === 'success'}
      >
        {getBackdropContent()}
      </Backdrop>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </FormProvider>
  );
}
